/**
 * useForecastFlow.js
 * ─────────────────────────────────────────────────────────────
 * IJ:       IJ-255 · Phase 5F · Forecast Handlers Hook
 * Date:     2026-08-02
 * Umbrella: IJ-076 · Refactor Programme 2026 · Phase 5F
 * Owner:    E1
 *
 * Purpose:
 *   Extract two forecast-scoped handler functions out of
 *   KnowledgeGraphPage.jsx so ForecastResultCard consumes a hook API
 *   instead of a hand-rolled prop bag from the parent.
 *
 * Handlers owned:
 *   - handleGenerateForecast(selectedFile · includeFormulas · api · API)
 *     · async · POST /api/financial_forecast · multipart form-data
 *     · 180-second timeout · toast on success and error
 *   - handleDownloadForecast(excelB64OrId · title)
 *     · sync · decodes base64 to Blob · triggers .xlsx download
 *     · trackEvent for analytics on success
 *
 * The third forecast handler handleInlineForecastUpload already lives
 * in the useUploadFlow hook (landed in IJ-244 · Phase 5C) so it is out
 * of scope for this slice.
 *
 * Byte-verbatim on every line of both handler bodies. Zero behaviour
 * change. Constructor-args pattern threads api · API · toast ·
 * trackEvent · state values · setters so the handler bodies stay
 * character-identical to the pre-extraction version.
 */
export default function useForecastFlow({
  api,
  API,
  toast,
  trackEvent,
  selectedFile,
  setSelectedFile,
  includeFormulas,
  setIsForecastLoading,
  setForecastResult,
  setAwaitingForecastFile,
}) {
  const handleGenerateForecast = async () => {
    if (!selectedFile) { toast.error('Please select an Excel file'); return; }
    if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) { toast.error('Financial Forecast requires an Excel (.xlsx) file'); return; }
    setIsForecastLoading(true);
    setForecastResult(null);
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      const response = await api.post(`${API}/financial_forecast?include_formulas=${includeFormulas}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000
      });
      setForecastResult(response.data);
      setSelectedFile(null);
      setAwaitingForecastFile(false);
      document.getElementById('file-input').value = '';
      toast.success('Forecast generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate forecast');
    } finally {
      setIsForecastLoading(false);
    }
  };

  const handleDownloadForecast = (excelB64OrId, title) => {
    try {
      // If we have base64 data, decode and download directly — no HTTP needed
      if (excelB64OrId && excelB64OrId.length > 100) {
        const byteCharacters = atob(excelB64OrId);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${(title || 'PnL_Forecast').replace(/[\s—–]+/g, '_')}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        toast.success('Forecast downloaded');
        trackEvent('demo_excel_download', { title: (title || 'PnL_Forecast').slice(0, 64) });
      } else {
        toast.error('Forecast data not available — please regenerate');
      }
    } catch (error) {
      toast.error('Failed to download forecast');
    }
  };

  return { handleGenerateForecast, handleDownloadForecast };
}
